/** States in which a stock row is retired for good — no transfer, handover or edit brings it back. */
const RETIRED_STAV = ['Převedeno', 'Předaný zákazníkovi', 'Předaný dealerovi'];

/**
 * Does a stock row in this state still count as "the car is ours"? Mirrors the API's `liveStav`: only the
 * terminal states are retired, a blank / missing state is live (legacy rows; the headers show it as
 * Skladový). Unlike `canTransferStav` it does not need a state to be present.
 */
export function isLiveStav(stav: string | null | undefined): boolean {
  return !RETIRED_STAV.includes(stav ?? '');
}

/** May the "Převést do firmy…" action be offered for a car in this state? */
export function canTransferStav(stav: string | null | undefined): boolean {
  return !!stav && !RETIRED_STAV.includes(stav);
}

/**
 * The purchase price as the API wants it: the WHOLE text must be an amount ("1 500,5" → "1500.5").
 * null = not (yet) a valid amount.
 */
export function parseAmount(text: string): string | null {
  const clean = text.replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(clean)) return null;
  const amount = String(Math.round(Number(clean) * 100) / 100);
  // The receiving firma's purchase price column is 15 characters wide.
  return amount.length > 15 ? null : amount;
}

/** "2026-10-07 14:03:11" / "2026-10-07" → "7. 10. 2026". */
export function formatTransferDate(value: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  return m ? `${Number(m[3])}. ${Number(m[2])}. ${m[1]}` : '';
}

/** The customer line of the result toast. */
export function zakaznikNote(z: 'prevzat' | 'vytvoren' | null): string {
  if (z === 'prevzat') return ' Zákazník byl nalezen v cílové firmě.';
  if (z === 'vytvoren') return ' Zákazník byl v cílové firmě založen.';
  return '';
}

/** The toast after a retroactive transfer. */
export function zpetneNote(rezim: 'prevod' | 'zpetne_vytvoreno' | 'zpetne_propojeno', firma: string): string {
  return rezim === 'zpetne_propojeno'
    ? `Vozidlo bylo propojeno s evidencí firmy ${firma}.`
    : `Převod doplněn — vozidlo je teď v evidenci firmy ${firma}.`;
}

/** The suggested price as the amount field shows it ("300000" → "300 000"); '' when there is none. */
export function suggestedPrice(value: string | null | undefined): string {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 2 }).format(n) : '';
}

/** The warning under a VIN field when sister firmas of the group already hold the car (null = nothing to say). */
export function vinSkupinaWarning(holders: readonly { firma_nazev: string; kind: 'nove' | 'ojete' }[]): string | null {
  if (holders.length === 0) return null;
  const where = holders.map((h) => `${h.firma_nazev} (${h.kind === 'nove' ? 'nové' : 'ojeté'})`).join(', ');
  return `VIN už je v evidenci skupiny: ${where}. Jde-li o stejný vůz, požádejte jeho držitele, ať ho sem převede („Převést do firmy…“).`;
}
